/**
 * MessageThread — the reusable two-way message thread (Wave-2 Phase 1 #346). A PURE
 * presentational component: a scrollable message list (author-aligned, time, read ✓) plus a
 * composer. It owns NO data fetching — a container passes the messages + an `onSend`, so the
 * same component drives the gov ↔ pilot mission thread today and the Phase-2/3 threads later.
 *
 * Alignment + read receipts derive from `viewerRole`: the viewer's OWN messages sit on the
 * right with a "✓ read" once the OTHER party has read them; the other party's messages sit on
 * the left. Fluid width — fits the 360px COP rail and the operator-portal panel alike.
 */
import { useState, type FormEvent, type JSX } from 'react';
import type { MissionMessageView, ThreadParty } from '../api';

/** Compact, locale-free `HH:MM` from an ISO `…THH:MM:SSZ` stamp (deterministic for tests). */
function fmtTime(iso: string): string {
  // "2026-06-18T13:45:09Z" → "06-18 13:45 UTC"
  return iso.length >= 16 ? `${iso.slice(5, 10)} ${iso.slice(11, 16)} UTC` : iso;
}

/** Has the OTHER party read this (viewer-authored) message? Drives the read ✓. */
function readByOther(m: MissionMessageView, viewerRole: ThreadParty): boolean {
  return viewerRole === 'requestor' ? m.read_by_operator_at != null : m.read_by_requestor_at != null;
}

/** Default party labels — the gov ↔ pilot mission thread (preserved for the drone usage). A
 *  container that is NOT the mission thread (e.g. the agency-task thread, the inter-ops thread)
 *  passes its own `partyLabels` so it never inherits "Pilot". */
const PARTY_LABEL: Record<ThreadParty, string> = { requestor: 'EOC', operator: 'Pilot' };

export function MessageThread({
  messages,
  viewerRole,
  onSend,
  sending = false,
  disabled = false,
  disabledNote,
  emptyNote = 'No messages yet.',
  partyLabels = PARTY_LABEL,
  className,
}: {
  messages: MissionMessageView[];
  viewerRole: ThreadParty;
  onSend: (content: string) => void;
  sending?: boolean;
  /** Composer disabled (e.g. gov has no assigned operator to message yet). */
  disabled?: boolean;
  disabledNote?: string;
  emptyNote?: string;
  /** Author-role → display label. Defaults to the gov↔pilot mission labels; a non-mission
   *  container MUST pass its own so a message never renders as "Pilot" out of context. */
  partyLabels?: Record<ThreadParty, string>;
  className?: string;
}): JSX.Element {
  const [draft, setDraft] = useState('');
  const canSend = !disabled && !sending && draft.trim().length > 0;

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (!canSend) return;
    onSend(draft.trim());
    setDraft('');
  }

  return (
    <div className={`flex flex-col gap-2 ${className ?? ''}`} data-testid="message-thread">
      <ul className="flex max-h-72 flex-col gap-1.5 overflow-y-auto" data-testid="message-list">
        {messages.length === 0 ? (
          <li className="text-[11px] text-slate-500">{emptyNote}</li>
        ) : (
          messages.map((m) => {
            const mine = m.author_role === viewerRole;
            return (
              <li
                key={m.id}
                className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}
                data-testid="message-item"
                data-author={m.author_role}
                data-mine={mine ? 'true' : 'false'}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-[12px] ${
                    mine
                      ? 'bg-cyan-600/30 border border-cyan-500/40 text-slate-100'
                      : 'bg-slate-800/70 border border-slate-700 text-slate-200'
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">{m.content}</p>
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 px-1 text-[9px] text-slate-500">
                  <span>{partyLabels[m.author_role]}</span>
                  <span className="font-mono">{fmtTime(m.created_at)}</span>
                  {mine && readByOther(m, viewerRole) && (
                    <span className="text-cyan-400" title="Read" data-testid="message-read">
                      ✓ read
                    </span>
                  )}
                </div>
              </li>
            );
          })
        )}
      </ul>

      {disabled ? (
        <p className="text-[11px] text-slate-500" data-testid="composer-disabled">
          {disabledNote ?? 'Messaging unavailable.'}
        </p>
      ) : (
        <form onSubmit={submit} className="flex items-end gap-1.5" data-testid="message-composer">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            placeholder="Write a message…"
            data-testid="message-input"
            className="min-w-0 flex-1 resize-none rounded border border-slate-700 bg-slate-950/60 px-2 py-1 text-[12px] text-slate-100 placeholder:text-slate-500 focus:border-slate-500 focus:outline-none"
          />
          <button
            type="submit"
            disabled={!canSend}
            data-testid="message-send"
            className="shrink-0 rounded bg-cyan-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </form>
      )}
    </div>
  );
}
